import type { DefaultValues, FieldValues } from 'react-hook-form';

import { useBaseForm } from '@pesto/hooks/src/useBaseForm';
import { useDeepCompareEffect } from '@pesto/hooks/src/useDeepCompareEffect';
import { useRef } from 'react';

import type { FieldConfig, FieldGroup, SmartFormProps } from './types';

import { Button } from '../../components/Button';
import { Form } from '../Form/Form';
import { SmartFormField } from './SmartFormField';
import { useConditionalFields } from './useConditionalFields';

export const SmartForm = <T extends FieldValues>({
  config,
  onSubmit,
  loading = false,
  formError,
  submitLabel = 'Submit',
  debug = false,
  children,
  bottomContent,
  mode = 'create',
  showResetButton,
  resetLabel = 'Reset',
  onReset,
  requireDirtySubmit,
}: SmartFormProps<T>) => {
  const form = useBaseForm<T>({
    schema: config.schema,
    defaultValues: config.defaultValues,
  });

  const { handleSubmit, formData, formState, setValue, watch } = form;

  const { errors } = formState;

  const initialValuesRef = useRef<T | DefaultValues<T>>(form.getValues());

  useDeepCompareEffect(() => {
    const defaults = config.defaultValues;

    if (defaults) {
      initialValuesRef.current = defaults;
      form.reset(defaults);
    }
  }, [config.defaultValues]);

  const { visibleFields, visibleFieldGroups } = useConditionalFields(
    config.fields || [],
    config.fieldGroups || [],
    watch,
  );

  if (debug) {
    console.log(`formDebug`, {
      formData,
      errors,
      visibleFields,
      visibleFieldGroups,
    });
  }

  const renderField = (field: FieldConfig<T>) => {
    const handleOnChange = (value: any) => {
      setValue(field.name, value);
    };

    const fieldError = form.getFieldState(field.name, formState).error;

    return (
      <SmartFormField
        key={field.name}
        field={field}
        value={formData[field.name] ?? field.defaultValue}
        onChange={handleOnChange}
        error={fieldError}
      />
    );
  };

  const renderFieldGroup = (group: FieldGroup<T>) => {
    return (
      <div key={group.name} className={`field-group ${group.className || ''}`}>
        {group.label && (
          <div className="field-group-header mb-4">
            <h3 className="text-lg font-medium text-foreground">{group.label}</h3>
            {group.description && <p className="mt-1 text-sm text-muted-foreground">{group.description}</p>}
          </div>
        )}
        <div className={getFieldGroupLayout(group.fields.length)}>{group.fields.map(renderField)}</div>
      </div>
    );
  };

  const getFieldGroupLayout = (fieldCount: number): string => {
    const layout = config.layout || 'vertical';
    const columns = config.columns || 2;

    switch (layout) {
      case 'horizontal':
        return 'flex flex-wrap gap-4';
      case 'grid':
        return `grid grid-cols-1 md:grid-cols-${Math.min(columns, fieldCount)} gap-4`;
      case 'vertical':
      default:
        return 'space-y-4';
    }
  };

  const renderErrorMessage = () => {
    if (!formError) {
      return null;
    }

    return <div className="mt-2 text-sm text-destructive">{formError}</div>;
  };

  const hasFieldGroups = config.fieldGroups && config.fieldGroups.length > 0;
  const hasFields = config.fields && config.fields.length > 0;

  const isFormDirty = Boolean(formState?.isDirty);
  const shouldRequireDirty = requireDirtySubmit ?? mode === 'edit';
  const shouldShowReset = (showResetButton ?? mode === 'edit') && isFormDirty;
  const isSubmitDisabled = loading || (shouldRequireDirty && !isFormDirty);
  const isResetDisabled = loading || !isFormDirty;

  const handleResetClick = () => {
    form.reset(initialValuesRef.current);
    onReset?.(form);
  };

  return (
    <Form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
      {children?.(form)}

      {hasFieldGroups && <div className="space-y-6">{visibleFieldGroups.map(renderFieldGroup)}</div>}

      {hasFields && (
        <div className={hasFieldGroups ? 'mt-6' : ''}>
          <div className={getFieldGroupLayout(visibleFields.length)}>{visibleFields.map(renderField)}</div>
        </div>
      )}

      {renderErrorMessage()}

      {bottomContent ? (
        bottomContent(form)
      ) : (
        <div className={'mt-6 flex w-full justify-end gap-2'}>
          {shouldShowReset && (
            <Button type="button" variant="outline" onClick={handleResetClick} disabled={isResetDisabled}>
              {resetLabel}
            </Button>
          )}
          <Button type="submit" loading={loading} disabled={isSubmitDisabled}>
            {config.submitLabel || submitLabel}
          </Button>
        </div>
      )}
    </Form>
  );
};
